import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useVideo } from '../../contexts/VideoContext';
import './LandingPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

const LandingPage = () => {
  const { daycareId } = useParams();
  const navigate = useNavigate();
  const { updateVideoData } = useVideo();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [daycare, setDaycare] = useState(null);

  useEffect(() => {
    const fetchDaycareInfo = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/daycare/${daycareId}`
        );

        const daycareData = response.data;
        setDaycare(daycareData);

        // Update context with daycare branding
        updateVideoData({
          daycareId: daycareData.daycareId,
          daycareName: daycareData.daycareName,
          logoUrl: daycareData.brandingConfig?.logoUrl || '',
          brandColor: daycareData.brandingConfig?.brandColor || '#FF6B35',
        });

        setLoading(false);
      } catch (err) {
        console.error('Error fetching daycare info:', err);
        setError('Could not load daycare information');
        setLoading(false);
      }
    };

    if (daycareId) {
      fetchDaycareInfo();
    } else {
      setLoading(false);
    }
  }, [daycareId, updateVideoData]);

  const handleGetStarted = () => {
    if (daycareId) {
      navigate(`/app/create/${daycareId}`);
    } else {
      navigate('/app/create/demo');
    }
  };

  if (loading) {
    return (
      <div className="landing-page loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="landing-page error">
        <h2>Oops!</h2>
        <p>{error}</p>
      </div>
    );
  }

  const brandColor = daycare?.brandingConfig?.brandColor || '#FF6B35';

  return (
    <div className="landing-page" style={{ '--brand-color': brandColor }}>
      <div className="landing-hero">
        {/* Daycare Logo */}
        {daycare?.brandingConfig?.logoUrl ? (
          <img
            src={daycare.brandingConfig.logoUrl}
            alt={daycare.daycareName}
            className="daycare-logo"
          />
        ) : (
          <div className="logo-placeholder">
            <span className="logo-icon">🐕</span>
          </div>
        )}

        {/* Welcome Message */}
        <h1 className="landing-title">
          {daycare?.daycareName
            ? `Welcome to ${daycare.daycareName}!`
            : 'Welcome to BarkBacks!'}
        </h1>

        <p className="landing-subtitle">
          See what your pup did today in their own words!
        </p>

        <p className="landing-description">
          Create a personalized AI-powered video of your dog telling you about their day.
          Choose their emotion, activities, and watch them come to life!
        </p>

        {/* Call to Action */}
        <button
          className="cta-button"
          onClick={handleGetStarted}
          style={{ backgroundColor: brandColor }}
        >
          Create Your Video
          <span className="cta-icon">🎬</span>
        </button>

        {/* Features */}
        <div className="features">
          <div className="feature">
            <span className="feature-icon">🐾</span>
            <h3>Personalized</h3>
            <p>Upload your dog's photo or choose from breeds</p>
          </div>

          <div className="feature">
            <span className="feature-icon">🎭</span>
            <h3>Choose Emotions</h3>
            <p>Happy, excited, playful, tired, and more!</p>
          </div>

          <div className="feature">
            <span className="feature-icon">🎥</span>
            <h3>AI-Powered</h3>
            <p>Photorealistic videos with lip-sync</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="landing-footer">
        <p>Powered by BarkBacks</p>
      </div>
    </div>
  );
};

export default LandingPage;
